/**
 * Explicit "the plot viewport moved" broadcast.
 *
 * Overlays that position DOM against the live uPlot instance (manual alert
 * badges, case annotations) have to recompute whenever the plot's scales move,
 * or they render at coordinates that describe the *previous* window — a badge
 * sitting at the wrong timestamp.
 *
 * They used to get that recompute for free, by accident: every range change
 * baked new auto y-range numbers into the uPlot options, which changed
 * `uPlotRecreateKey`, which destroyed and recreated the instance, which forced
 * the overlays to re-read positions. Ranges are now applied in place via
 * `setScale` (and tweened across frames), so the viewport can move without
 * anything in the React tree changing. That movement is published here instead.
 *
 * Deliberately a module-level store rather than props: the overlays live in
 * sibling subtrees (`MdDraftAlertCaseFlow`) that never receive the uPlot
 * lifecycle as props, and the intermediate component is owned elsewhere.
 *
 * Over-notifying is safe by construction. Subscribers re-read positions from
 * the live instance, so a spurious notification costs one recompute and can
 * never produce a wrong position; a *missed* notification is the failure that
 * matters, so this errs toward firing.
 */

let revision = 0
const listeners = new Set<() => void>()

export const getChartViewportRevision = (): number => revision

export const subscribeChartViewport = (listener: () => void): (() => void) => {
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}

export const notifyChartViewportChanged = (): void => {
  revision += 1
  listeners.forEach((listener) => listener())
}

/**
 * Distinguishes an x-range WE pushed from one the user dragged.
 *
 * uPlot's `setScale` hook cannot tell a programmatic `setScale` from a
 * drag-to-zoom, and the viewport transition drives `setScale` once per
 * animation frame. Every frame therefore looked like a fresh user zoom: the
 * hook committed a new `timeRange`, which issued a fetch, which was aborted by
 * the next frame's fetch ~50ms later. One gesture produced dozens of cancelled
 * requests, the spinner never settled, and no data landed.
 *
 * A time-scoped guard ("are we applying right now?") CANNOT work: uPlot flushes
 * its hooks via `queueMicrotask`, so the hook always runs after the synchronous
 * apply has returned. The guard must instead be value-scoped — record the exact
 * range we set, and recognise it whenever the deferred hook eventually fires.
 *
 * Keyed by whole-millisecond min|max: uPlot stores an explicit setScale value
 * verbatim, and rounding absorbs float noise without risking a real collision
 * (two distinct user windows a sub-millisecond apart do not occur).
 */
const programmaticXRanges = new Set<string>()

// A tween is ~16 frames; a runaway must not leak unboundedly. Well above any
// real burst, low enough to bound memory.
const MAX_TRACKED_RANGES = 512

const rangeKey = (min: number, max: number): string =>
  `${Math.round(min)}|${Math.round(max)}`

/** Record an x-range we are about to apply, so its deferred hook is recognised. */
export const markProgrammaticXRange = (min: number, max: number): void => {
  if (programmaticXRanges.size >= MAX_TRACKED_RANGES) {
    programmaticXRanges.clear()
  }
  programmaticXRanges.add(rangeKey(min, max))
}

/**
 * True (and forgets the range) if this x-range is one we pushed. A user zoom
 * lands on a value we never set, so it is not recognised and passes through.
 */
export const consumeProgrammaticXRange = (
  min: number,
  max: number
): boolean => {
  const key = rangeKey(min, max)
  return programmaticXRanges.delete(key)
}

/** Test seam. */
export const resetProgrammaticXRanges = (): void => {
  programmaticXRanges.clear()
}
